import { useState, useRef, useEffect, useCallback } from "react";
import { ReplyFill } from "react-bootstrap-icons";
import { ChatMessageWrapperProps } from "./message-item-wrapper.props";

export const ChatMessageWrapper = ({
  msg,
  idx,
  onReply,
  children,
  isUserMsg,
  messageRefs,
  isFirstUnread,
  highlightedMessageId,
  isRapidClicking,
  modeSelected,
  onSelectMessage,
  onMouseDown,
  onMouseEnter,
  onMouseUp,
}: ChatMessageWrapperProps) => {
  const [dragX, setDragX] = useState(0);
  const [replyActive, setReplyActive] = useState(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const draggingRef = useRef(false);
  const isHorizontalDrag = useRef(false);
  const elementRef = useRef<HTMLDivElement>(null);

  const MAX_LEFT = 50;
  const THRESHOLD = 35;
  const HORIZONTAL_SWIPE_ANGLE = 10;

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (modeSelected) return;
    const touch = e.touches[0];
    if (!touch) return;
    startXRef.current = touch.clientX;
    startYRef.current = touch.clientY;
    draggingRef.current = true;
    isHorizontalDrag.current = false;
  }, [modeSelected]);

const handleTouchMove = useCallback((e: TouchEvent) => {
  if (!draggingRef.current || modeSelected) return;
  const touch = e.touches[0];
  if (!touch) return;

  const deltaX = touch.clientX - startXRef.current;
  const deltaY = touch.clientY - startYRef.current;

  if (Math.abs(deltaX) > Math.abs(deltaY) + HORIZONTAL_SWIPE_ANGLE) {
    isHorizontalDrag.current = true;

    if (e.cancelable) {
      e.preventDefault();
    }

    if (deltaX < 0) {
      const limitedX = Math.max(deltaX, -MAX_LEFT);
      setDragX(limitedX);

      setReplyActive(Math.abs(limitedX) > THRESHOLD);
    } else {
      setDragX(Math.min(deltaX, 0));
      setReplyActive(false);
    }
  }
}, [modeSelected, MAX_LEFT, THRESHOLD]);

const handleTouchEnd = useCallback(() => {
  if (replyActive) {
    onReply();
  }

  if (dragX === -MAX_LEFT && "vibrate" in navigator) {
    navigator.vibrate(10);
  }

  setDragX(0);
  setReplyActive(false);
  draggingRef.current = false;
  isHorizontalDrag.current = false;
}, [replyActive, dragX, onReply]);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false }); 
    el.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <div
      key={`${msg._id}-${idx}`}
      ref={(el) => {
        elementRef.current = el;
        if (el) {
          messageRefs.current[msg._id] = el;
        }
      }}
      className={`${
        highlightedMessageId === msg._id ? "message-highlighted" : ""
      } ${isRapidClicking ? "rapid" : ""}`}
      data-msg-id={msg._id}
      style={{
        transform: `translateX(${dragX}px)`,
        transition: draggingRef.current ? "none" : "transform 0.2s ease",
        position: "relative",
        touchAction: "pan-y",
      }}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseUp={onMouseUp}
    >
      {isFirstUnread && (
        <div
          className="text-center text-secondary mb-2"
          style={{
            position: "relative",
            top: "-2px",
            fontSize: "12px",
            fontWeight: 500,
          }}
        >
          — Непрочитанные сообщения —
        </div>
      )}

      {replyActive && (
        <div
          style={{
            position: "absolute",
            bottom: 4,
            transform: "translateY(-50%)",
            right: -30,
          }}
        >
          <ReplyFill size={20} />
        </div>
      )}

      {children}
    </div>
  );
};